<template>
  <div v-if="problem && problem.id" class="problem-actions-section">
    <div class="v-centerer problem-sheet__section-title-wrapper">
      <h2 class="basic-sub-title problem-sheet__section-title">
        {{ $t('message.ACTIONS') }}
      </h2>

      <ProblemTrust :trust="actionsTrust * 100" @click="App.showIA" />
    </div>

    <div class="problem-actions-section__content">
      <div>
        <Checkbox
          :value="selectAllActions"
          class="block ml10em"
          :title="$t('message.SHOW_ALL_ACTIONS')"
          :disabled="selectAllActions"
          @input="$emit('selectAllActions', $event)"
        />
      </div>
      <div>
        <PlanActionItem
          v-for="action in nonSecuringActions"
          :key="`${action.id}-${actionsKey}`"
          :action="action"
          :all-users="allTeams"
          :n-lines="currentTeam.planActionLines"
          :is-quick-action="action.isQuickAction === 1"
          hide-problem-data
          delete-btn-on-hover
          @pdcaEdit="sendSocketUpdate"
          @leaderEdit="sendSocketUpdate"
          @deleteClick="deleteAction(action)"
        />
        <button
          v-if="!disabled"
          class="generic-border generic-border--text-btn generic-border--blue-border mt10em f-w"
          @click="addAction()"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ icons.addExtraBold }}</Icon>
            <span>{{ $t('message.ADD_AN_ACTION') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
        </button>

        <div class="legend legend--bottom-margin problem-actions-section__securing-actions-legend">
          {{ $t('message.SECURING_ACTIONS') }}
        </div>
        <ActionItem
          v-for="(action, index) in securingActions"
          :key="`sec-${action.id}-${actionsKey}`"
          :value="action"
          :disabled="disabled"
          :problem-affect-team="problem.affectTeam || 0"
          :assignable-teams="allTeams.map(team => team.id)"
          @fieldInput="editAction(action, $event)"
          @deleteClick="deleteAction(action, index)"
          @updatePlans="updatePlans()"
        />
        <button
          v-if="!disabled"
          class="generic-border generic-border--text-btn generic-border--blue-border mt10em f-w"
          @click="addAction(true)"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ icons.addExtraBold }}</Icon>
            <span>{{ $t('message.ADD_SECURING_ACTION') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Actions section of the problem sheet
 *
 * @vue-prop {Object} problem - Problem object
 * @vue-prop {Array} actionsPdcaFilter - Array of PDCA values to show in the section
 * @vue-prop {Number} [selectedCauseId] - Cause selected to filter actions
 * @vue-prop {Number} [selectAllActions=true] - Whether all actions should be displayed
 * @vue-prop {Boolean} [disabled] - Whether the problem sheet is editable
 */
import toastr from 'toastr';
// Vuex
import { ADD_ACTION, EDIT_ACTION, DELETE_ACTION } from '@/store/modules/problem/action-types';
// Inport components
import ActionItem from '@/components/action-item.vue';
import Checkbox from '@/components/checkbox.vue';
import Icon from '@/components/icon.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
import ProblemTrust from '@/components/problem-sheet/problem-trust.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { calert, nestedFlatten } from '@/utils/main';
import * as icons from '@/assets/icons';

export default {

  name: 'ProblemActionsSection',

  components: {
    ActionItem,
    Checkbox,
    Icon,
    PlanActionItem,
    ProblemTrust,
  },

  mixins: [mixinSocketHandler],

  props: {
    problem: {
      type: Object,
      required: true,
    },
    actionsPdcaFilter: {
      type: Array,
      required: true,
    },
    selectedCauseId: {
      type: Number,
      default: null,
    },
    selectAllActions: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
  },

  data() {
    return {
      icons,
      // Items to pick from
      zonesTree: [],
      allZones: [],
      allIndicators: [],
      allTypes: [],
      allKeywords: [],
      allPlans: [],
      // Actions filters
      actionsKey: 1,
      // Actions per cause modal from 5why
      currentCause: null,
    };
  },

  computed: {
    /** All the causes for this problem in a linear array */
    allCauses() {
      return nestedFlatten(this.problem.wCauses || [], 'wCauses');
    },
    /** Object. Key: Cause ID ; Value: Cause object */
    causesById() {
      return this.allCauses.reduce((all, cause) => ({ ...all, [`${cause.id}`]: cause }), {});
    },
    actionsToDisplay() {
      return this.selectAllActions
        ? this.problem.actions || []
        : this.actionsForCause(this.selectedCauseId);
    },
    /** Securing actions are not part of the problem resolution => bottom */
    securingActions() {
      return this.actionsToDisplay.filter(a => a.isSecuring === 1);
    },
    /** Resolution actions */
    nonSecuringActions() {
      const actions = this.actionsToDisplay.filter(a => a.isSecuring === 0);
      return this.selectAllActions
        ? actions
        : actions.filter(a => this.actionsPdcaFilter.includes(a.pdca));
    },
    /** Number: Between 0 & 1 the trust index of actions */
    actionsTrust() {
      if (this.allCauses.length === 0 || this.problem.actions.length === 0) {
        return 0;
      }
      const trustedActions = this.problem.actions
        .filter((action) => {
          const cause = this.causesById[`${action.cause}`];
          return cause && Number(cause.isReal) === 1 && Number(cause.isVerified) === 1;
        });
      return trustedActions.length / this.problem.actions.length;
    },
    /** Array: Users of the teams assigned to this problem */
    allUsers() {
      return this.$store.getters['problem/radioUsers'](this.problem.id);
    },
    /** Array: assignable teams for this problem */
    allTeams() {
      return this.$store.getters['problem/teamsForProblem'](this.problem.id);
    },
  },

  methods: {
    getCauseWithId(id, array) {
      array = array || this.problem.wCauses;
      for (let i = 0, len = array.length; i < len; i++) {
        const cause = array[i];
        if (id === cause.id) {
          return cause;
        }
        const child = this.getCauseWithId(id, cause.wCauses);
        if (child != null) {
          return child;
        }
      }
      return null;
    },
    /** Add an action to a cause or to the problem if no cause is selected */
    addAction(isSecuring, causeId) {
      const params = {
        id: causeId || (this.selectedCauseId || 0),
        problemId: this.problem.id,
      };
      params.isSecuring = isSecuring ? true : undefined;
      this.$store.dispatch(`problem/${ADD_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data instanceof Object && data.id && data.problem > 0) {
          this.sendSocketUpdate();
        } else if (data === -2) {
          calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
        } else {
          calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
        }
        if (causeId) {
          this.displayActionInModal(data.id);
        }
      });
    },
    /** Set an action to another cause */
    editAction(action, fields) {
      const params = { id: action.id, fromProblemSheet: 1 };
      if (!(fields instanceof Array)) {
        fields = [fields];
      }
      fields.forEach((field) => {
        if (action[field] !== undefined) {
          params[field] = action[field];
        }
      });
      this.$store.dispatch(`problem/${EDIT_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          toastr.remove();
          toastr.success(this.$t('message.ACTION_UPDATED'));
          this.sendSocketUpdate();
          this.sendSocketMessage({
            team: data.affectTeam,
            id: data.id,
            msg: SocketMessages.CC_MSGS.ACTION_EDITION,
          });
          this.sendSocketMessage({
            team: data.affectTeam,
            msg: data.plan === 0 ? SocketMessages.CC_MSGS.PENDING : SocketMessages.CC_MSGS.ACTION_PLANS,
          });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Delete the action from server */
    confirmDeleteAction(action) {
      const params = { id: action.id, problemId: this.problem.id };
      this.$store.dispatch(`problem/${DELETE_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data) {
          toastr.success(this.$t('message.ACTION_DELETED'));
          this.sendSocketMessage(Number(action.plan) === 0
            ? { msg: SocketMessages.CC_MSGS.PENDING_AND_PROBLEMS, id: this.problem.id }
            : { msg: SocketMessages.CC_MSGS.PROBLEMS_AND_PLANS, id: this.problem.id });
          this.sendSocketUpdate();
        } else if (Number(data) === -1) {
          toastr.error(this.$t('message.ACTION_DELETABLE_ASSIGNED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Ask the user to confirm action deletion */
    deleteAction(action, index) {
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE_ACTION'),
          action: () => {
            this.confirmDeleteAction(action, index);
          },
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Get actions linked to a cause */
    actionsForCause(causeId) {
      if (causeId && this.problem.actions) {
        return this.problem.actions.filter(a => a.cause === causeId);
      }
      return this.problem.actions || [];
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEM_SHEET, id: this.problem.id, socketCompany: 1 });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.problem-actions-section {

  .plan-action-item, .action-item {
    width: 100%;
    box-sizing: border-box;
  }

  &__securing-actions-legend {
    margin-top: 3.5rem;
  }

  &__content {
    position: relative;
    box-sizing: border-box;
  }
}
</style>
